<template>
  <v-card>
    <v-card-title class="flex-wrap">
      <span>Project Metadata</span>
      <v-spacer />
      <v-chip small class="mr-2" color="grey lighten-3">
        {{ totalProjects }} loaded
      </v-chip>
      <v-chip v-if="dirtyCount" small color="blue lighten-4">
        {{ dirtyCount }} unsaved
      </v-chip>
    </v-card-title>

    <v-card-text>
      <v-row dense align="center">
        <v-col cols="12" md="8">
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search by project name"
            single-line
            hide-details
            dense
            outlined
            clearable
          />
        </v-col>

        <v-col cols="12" md="4" class="text-right">
          <v-btn
            color="primary"
            :disabled="dirtyCount === 0 || savingAll"
            :loading="savingAll"
            @click="saveAllDirty"
          >
            Save all changes ({{ dirtyCount }})
          </v-btn>
        </v-col>
      </v-row>

      <v-expand-transition>
        <v-card v-if="selected.length" outlined class="mt-4 pa-3 bulk-toolbar">
          <div class="subtitle-2 mb-2">{{ selected.length }} project(s) selected</div>
          <v-row dense align="center">
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="bulkMarket"
                :items="countries"
                item-text="title"
                item-value="title"
                label="Set market"
                dense
                hide-details
                outlined
                clearable
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-combobox
                v-model="bulkStudyType"
                :items="studyTypes"
                label="Set study type"
                dense
                hide-details
                outlined
                clearable
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="bulkCategory"
                :items="categories"
                label="Set category"
                dense
                hide-details
                outlined
                clearable
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-btn color="primary" block @click="applyBulkToSelected">Apply to selected</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-expand-transition>

      <v-progress-linear
        v-if="savingAll"
        :value="saveProgress"
        height="6"
        color="primary"
        class="mt-4"
      />

      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="filteredProjects"
        item-key="_id"
        show-select
        :items-per-page="limit"
        hide-default-footer
        class="elevation-1 mt-4 metadata-table"
        :item-class="rowClass"
      >
        <template v-slot:[`item.name`]="{ item }">
          <div class="project-name">
            {{ item.name }}
            <v-icon v-if="isComplete(item)" small color="green" class="ml-1">check_circle</v-icon>
          </div>
        </template>

        <template v-slot:[`item.market`]="{ item }">
          <v-autocomplete
            :value="item.market"
            :items="countries"
            item-text="title"
            item-value="title"
            dense
            hide-details
            placeholder="Market"
            class="table-field"
            clearable
            @change="value => updateField(item, 'market', value)"
          />
        </template>

        <template v-slot:[`item.studyType`]="{ item }">
          <v-combobox
            :value="item.studyType"
            :items="studyTypes"
            dense
            hide-details
            placeholder="Study type"
            class="table-field"
            @change="value => updateField(item, 'studyType', value)"
          />
        </template>

        <template v-slot:[`item.category`]="{ item }">
          <v-autocomplete
            :value="item.category"
            :items="categories"
            dense
            hide-details
            placeholder="Category"
            class="table-field"
            clearable
            @change="value => updateField(item, 'category', value)"
          />
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <a
            :href="`/buyer/configureProject/${item._id}`"
            target="_blank"
            rel="noopener noreferrer"
            class="see-questions-link mr-2"
          >
            See questions
          </a>
          <v-btn
            x-small
            color="primary"
            :disabled="!item._dirty || item._saving"
            :loading="item._saving"
            @click="saveProject(item)"
          >
            Save
          </v-btn>
        </template>
      </v-data-table>

      <v-row class="mt-4" align="center">
        <v-col cols="12" md="6">
          <div class="caption">
            Showing {{ filteredProjects.length }} of {{ totalProjects }} projects
          </div>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end">
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="7"
            @input="loadProjects"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import RestResource from "@/services/dataServiceBuyer.js";

const service = new RestResource();

const STUDY_TYPES = [
  "CX experience Satisfaction Study",
  "BHT",
  "U&A",
  "Conncept Idea Test",
  "Product test",
  "brand lift study",
  "Qualitative exploratory",
  "Shopper Research (quant or qual)",
];

const PROJECT_CATEGORIES = [
  "Advertisement",
  "Alcohol, Beers, Fashion",
  "Attributes",
  "Baby Milk",
  "Baby Pampers",
  "Babycare",
  "Baking Supplies",
  "Banking",
  "Beverages",
  "Beverages, Media, Transportation",
  "Body Care",
  "Brand Boycott",
  "Cannabis",
  "Cigarette",
  "Cleaning Agent",
  "Corporations",
  "Detergent",
  "Digital Media",
  "E-commerce / Ecommerce / Eccommerce",
  "Education",
  "Electronics",
  "Engine Oil",
  "Events",
  "Fashion",
  "Fast Food",
  "Financial / Financial Products",
  "Flight",
  "Food / Foods",
  "Food Delivery / Food Delivery Apps / Food Order Apps",
  "Gaming",
  "HairCare",
  "Home Appliances",
  "Hotels",
  "Household",
  "Household Appliances",
  "Household Cleaners",
  "Internet Providers",
  "Kitchenware",
  "Media Planning",
  "Milk",
  "Milk Powder",
  "Payment Method",
  "Personal Care",
  "Personal Hygiene",
  "Pet",
  "Pet food",
  "Petrochemical Companies",
  "Petrol Stations",
  "Property",
  "Public Infrastructure",
  "Public Transport",
  "Real Estate",
  "Skincare",
  "Skincare & Personal Care",
  "Smartphone",
  "Social Impact Study",
  "Sports",
  "Supermarket",
  "Supplements",
  "TV Channel",
  "Telcommunications / Telecommunications",
  "Tissue",
  "Toys",
  "Transportation",
  "Travel",
  "Vaccines",
  "Vapes",
  "Water Purifier",
];

function snapshot(project) {
  return JSON.stringify({
    market: project.market || "",
    studyType: project.studyType || "",
    category: project.category || "",
  });
}

function normalizeMarket(market) {
  if (Array.isArray(market)) return market[0] || "";
  return market || "";
}

export default {
  data() {
    return {
      projects: [],
      originalSnapshots: {},
      countries: [],
      studyTypes: STUDY_TYPES,
      categories: PROJECT_CATEGORIES,
      search: "",
      selected: [],
      bulkMarket: "",
      bulkStudyType: "",
      bulkCategory: "",
      page: 1,
      limit: 20,
      totalProjects: 0,
      totalPages: 1,
      savingAll: false,
      saveProgress: 0,
      headers: [
        { text: "Project Name", value: "name", width: "22%" },
        { text: "Market", value: "market", sortable: false, width: "22%" },
        { text: "Study Type", value: "studyType", sortable: false, width: "20%" },
        { text: "Category", value: "category", sortable: false, width: "20%" },
        { text: "Actions", value: "actions", sortable: false, width: "16%" },
      ],
    };
  },

  computed: {
    dirtyCount() {
      return this.projects.filter((project) => project._dirty).length;
    },

    filteredProjects() {
      const query = (this.search || "").trim().toLowerCase();

      return this.projects.filter((project) => {
        if (query && !(project.name || "").toLowerCase().includes(query)) return false;
        return true;
      });
    },
  },

  mounted() {
    this.loadConfigs();
    this.loadProjects();
  },

  methods: {
    loadConfigs() {
      service.getConfig("countries").then((response) => {
        this.countries = response.data || [];
      });
    },

    loadProjects(inputPage) {
      const requestedPage = Number(inputPage) || this.page || 1;
      this.$setLoader();
      service.fetchProjectsWithResponses({
        page: requestedPage,
        limit: this.limit,
      }).then((response) => {
        const payload = response.data || {};
        const apiProjects = Array.isArray(payload)
          ? payload
          : (payload.projects || payload.data || payload.rows || []);

        const totalProjects = payload.total
          || payload.totalCount
          || payload.count
          || (payload.pagination && payload.pagination.total)
          || apiProjects.length;

        const currentPage = payload.page || payload.currentPage || requestedPage;
        const currentLimit = payload.limit || payload.perPage || this.limit;
        const projects = (apiProjects || []).map((project) => ({
          ...project,
          market: normalizeMarket(project.market || project.country),
          studyType: project.studyType || "",
          category: project.category || "",
          _dirty: false,
          _saving: false,
        }));

        this.page = currentPage;
        this.limit = currentLimit;
        this.totalProjects = totalProjects;
        this.totalPages = Math.max(1, Math.ceil(totalProjects / currentLimit));
        this.projects = projects;
        this.selected = [];
        this.originalSnapshots = {};
        projects.forEach((project) => {
          this.originalSnapshots[project._id] = snapshot(project);
          if (project.category && !this.categories.includes(project.category)) {
            this.categories.push(project.category);
          }
          if (project.studyType && !this.studyTypes.includes(project.studyType)) {
            this.studyTypes.push(project.studyType);
          }
        });
        this.$disableLoader();
      }).catch(() => {
        this.$disableLoader();
        alert("Failed to load projects");
      });
    },

    isComplete(project) {
      return !!project.market && !!project.studyType && !!project.category;
    },

    isDirty(project) {
      return this.originalSnapshots[project._id] !== snapshot(project);
    },

    updateField(project, field, value) {
      this.$set(project, field, value || "");
      this.$set(project, "_dirty", this.isDirty(project));
    },

    rowClass(item) {
      if (item._dirty) return "row-dirty";
      if (!this.isComplete(item)) return "row-incomplete";
      return "";
    },

    applyBulkToSelected() {
      if (!this.selected.length) return;

      const hasBulkValue = this.bulkMarket || this.bulkStudyType || this.bulkCategory;
      if (!hasBulkValue) {
        alert("Select at least one value to apply");
        return;
      }

      this.selected.forEach((project) => {
        if (this.bulkMarket) this.$set(project, "market", this.bulkMarket);
        if (this.bulkStudyType) this.$set(project, "studyType", this.bulkStudyType);
        if (this.bulkCategory) this.$set(project, "category", this.bulkCategory);
        this.$set(project, "_dirty", this.isDirty(project));
      });
    },

    buildUpdatePayload(project) {
      return {
        projectId: project._id,
        market: project.market,
        studyType: project.studyType,
        category: project.category,
        primaryUpdate: true,
      };
    },

    async saveProject(project) {
      this.$set(project, "_saving", true);

      try {
        await service.updateProjectMetadata(this.buildUpdatePayload(project));
        this.originalSnapshots[project._id] = snapshot(project);
        this.$set(project, "_dirty", false);
      } catch (error) {
        alert(`Failed to save ${project.name}`);
      } finally {
        this.$set(project, "_saving", false);
      }
    },

    async saveAllDirty() {
      const dirtyProjects = this.projects.filter((project) => project._dirty);
      if (!dirtyProjects.length) return;

      this.savingAll = true;
      this.saveProgress = 0;

      for (let index = 0; index < dirtyProjects.length; index += 1) {
        await this.saveProject(dirtyProjects[index]);
        this.saveProgress = Math.round(((index + 1) / dirtyProjects.length) * 100);
      }

      this.savingAll = false;
      this.saveProgress = 0;
      alert(`Saved ${dirtyProjects.length} project(s)`);
    },
  },
};
</script>

<style scoped>
.bulk-toolbar {
  background: #f5f9ff;
}

.metadata-table >>> .row-incomplete {
  background-color: #fff8e1;
}

.metadata-table >>> .row-dirty {
  background-color: #e3f2fd;
}

.project-name {
  font-weight: 500;
  line-height: 1.3;
}

.table-field {
  min-width: 140px;
}

.see-questions-link {
  color: #1976d2;
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;
}

.see-questions-link:hover {
  text-decoration: underline;
}
</style>
